import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Globe2, Loader2, Sparkles, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/architect`;

async function j(path, method = "GET", body) {
  const token = localStorage.getItem("arca_token");
  const res = await fetch(`${API}${path}`, {
    method,
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) { let d = "Request failed"; try { d = (await res.json()).detail || d; } catch { /* noop */ } throw new Error(d); }
  return res.json();
}

const CATS = [
  ["translation", "Translation"], ["accessibility", "Accessibility"], ["seo", "SEO"],
  ["security", "Security"], ["performance", "Performance"], ["launch_readiness", "Launch"],
];
const ring = (v) => (v >= 80 ? "#34d399" : v >= 60 ? "#00F0FF" : v >= 40 ? "#fbbf24" : "#f87171");
const SEV = { high: "text-red-400", medium: "text-amber-400", low: "text-slate-400" };

export default function GlobalReadinessPanel({ projectId, initialReadiness }) {
  const [data, setData] = useState(initialReadiness || null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setData(initialReadiness || null); }, [initialReadiness]);

  const run = useCallback(async () => {
    setBusy(true);
    try { const r = await j(`/projects/${projectId}/readiness`, "POST"); setData(r); toast.success("Global readiness scored"); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }, [projectId]);

  const score = data?.global_readiness_score ?? 0;
  const deg = (score / 100) * 360;

  return (
    <div className="mt-8 rounded-2xl border border-white/10 bg-[#0a0a0a] p-5" data-testid="readiness-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <p className="font-display font-bold text-lg flex items-center gap-2"><Globe2 size={18} className="text-[#00F0FF]" /> Global Launch Assistant</p>
        <button onClick={run} disabled={busy} className="inline-flex items-center gap-2 bg-[#0055FF] text-white px-5 py-2.5 rounded-xl font-semibold text-sm shadow-[0_0_25px_rgba(0,85,255,0.4)] disabled:opacity-40 hover:-translate-y-0.5 transition-transform duration-300" data-testid="readiness-run-btn">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} {data ? "Re-check" : "Check global readiness"}
        </button>
      </div>

      {!data && <p className="text-slate-500 text-sm">Verify translation, accessibility, SEO, security and performance and get a single Global Readiness Score before you publish worldwide.</p>}

      {data && (
        <div data-testid="readiness-result">
          <div className="flex items-center gap-5 flex-wrap">
            <div className="relative w-28 h-28 shrink-0" data-testid="readiness-score">
              <div className="w-full h-full rounded-full" style={{ background: `conic-gradient(${ring(score)} ${deg}deg, rgba(255,255,255,0.08) ${deg}deg)` }} />
              <div className="absolute inset-[10px] rounded-full bg-[#0a0a0a] flex flex-col items-center justify-center">
                <span className="font-display font-extrabold text-3xl" style={{ color: ring(score) }}>{score}</span>
                <span className="text-[9px] font-mono uppercase text-slate-500">/ 100</span>
              </div>
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className={`inline-flex items-center gap-1.5 text-sm font-semibold ${data.ready_to_launch ? "text-emerald-400" : "text-amber-400"}`} data-testid="readiness-verdict">
                {data.ready_to_launch ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />} {data.ready_to_launch ? "Ready to launch globally" : "Not ready to launch yet"}
              </div>
              <p className="text-slate-400 text-sm mt-1">{data.verdict}</p>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
            {CATS.map(([k, label]) => {
              const v = data.categories?.[k]?.score ?? 0;
              return (
                <div key={k} className="rounded-xl border border-white/10 bg-black/40 p-3" data-testid={`readiness-cat-${k}`}>
                  <div className="flex items-baseline justify-between"><span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</span><span className="font-display font-bold" style={{ color: ring(v) }}>{v}</span></div>
                  <div className="mt-1.5 h-1.5 rounded-full bg-white/10 overflow-hidden"><motion.div initial={{ width: 0 }} animate={{ width: `${v}%` }} transition={{ duration: 0.7 }} className="h-full" style={{ background: ring(v) }} /></div>
                  {data.categories?.[k]?.notes && <p className="text-slate-500 text-[10px] mt-1.5 leading-snug">{data.categories[k].notes}</p>}
                </div>
              );
            })}
          </div>

          {(data.recommendations || []).length > 0 && (
            <div className="mt-4 space-y-1.5" data-testid="readiness-recommendations">
              {data.recommendations.map((r, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  <XCircle size={13} className={`${SEV[r.severity] || SEV.low} mt-0.5 shrink-0`} />
                  <span className="text-slate-300"><span className={`font-mono text-[9px] uppercase ${SEV[r.severity] || SEV.low}`}>{r.severity} · {r.category}</span> — {r.action}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
